import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, User, Search, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useScrollReveal, useParallax } from '../hooks/useAnimations';
import { useTranslation } from 'react-i18next';

// Reusable animated section component
const AnimatedSection = ({ children, className = '', animation = 'reveal', style = {} }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div ref={ref} className={`${animation} ${isVisible ? 'visible' : ''} ${className}`} style={style}>
      {children}
    </div>
  );
};

const Landing = () => {
  const heroParallax = useParallax(0.3);
  const { t } = useTranslation();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const differentiators = [
    {
      id: 'priorities',
      title: t('differentiators.items.priorities.title'),
      description: t('differentiators.items.priorities.description')
    },
    {
      id: 'opportunities',
      title: t('differentiators.items.opportunities.title'),
      description: t('differentiators.items.opportunities.description')
    },
    {
      id: 'guidance',
      title: t('differentiators.items.guidance.title'),
      description: t('differentiators.items.guidance.description')
    }
  ];

  const steps = [
    {
      id: 'brief',
      number: '01',
      title: t('howItWorks.steps.brief.title'),
      description: t('howItWorks.steps.brief.description'),
      icon: FileText
    },
    {
      id: 'consultation',
      number: '02',
      title: t('howItWorks.steps.consultation.title'),
      description: t('howItWorks.steps.consultation.description'),
      icon: User
    },
    {
      id: 'search',
      number: '03',
      title: t('howItWorks.steps.search.title'),
      description: t('howItWorks.steps.search.description'),
      icon: Search
    },
    {
      id: 'selection',
      number: '04',
      title: t('howItWorks.steps.selection.title'),
      description: t('howItWorks.steps.selection.description'),
      icon: CheckCircle
    }
  ];

  const lifestyles = [
    {
      id: 'coastal',
      title: t('whyPortugal.lifestyles.coastal'),
      image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80'
    },
    {
      id: 'city',
      title: t('whyPortugal.lifestyles.city'),
      image: 'https://images.unsplash.com/photo-1585208798174-6cedd86e019a?w=800&q=80'
    },
    {
      id: 'countryside',
      title: t('whyPortugal.lifestyles.countryside'),
      image: 'https://images.unsplash.com/photo-1595521624992-48a59aef95e3?w=800&q=80'
    },
    {
      id: 'island',
      title: t('whyPortugal.lifestyles.island'),
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80'
    }
  ];

  return (
    <div data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image with Parallax */}
        <div 
          ref={heroParallax}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hero-parallax"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=1920&q=85')`,
            transform: `translateY(var(--parallax-offset, 0)) scale(1.1)`
          }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </div>

        {/* Content */}
        <div className="container-custom relative z-10 pt-20">
          <div className="max-w-3xl">
            <p className="text-white/70 text-sm tracking-[0.2em] uppercase mb-6 blur-in">
              {t('hero.subtitle')}
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white font-medium leading-[1.1] mb-8 blur-in" style={{animationDelay: '0.2s'}}>
              {t('hero.title')}
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl blur-in" style={{animationDelay: '0.4s'}}>
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 blur-in" style={{animationDelay: '0.6s'}}>
              <Link to="/start-your-search">
                <Button className="btn-primary btn-ripple text-base h-14 px-8" data-testid="hero-primary-cta">
                  {t('hero.cta')}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                onClick={() => scrollToSection('approach')}
                className="h-14 px-8 rounded-full font-medium border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all" 
                data-testid="hero-secondary-cta"
              >
                {t('hero.secondary')}
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button 
          onClick={() => scrollToSection('approach')}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 float cursor-pointer"
        >
          <ArrowDown size={24} className="text-white/60" />
        </button>
      </section>

      {/* Approach Section */}
      <section id="approach" className="section-padding bg-white" data-testid="approach-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <p className="text-primary text-sm tracking-[0.2em] uppercase font-medium mb-6">{t('approach.label')}</p>
              <h2 className="font-heading text-3xl md:text-5xl font-medium text-foreground mb-8 leading-tight">
                {t('approach.title')}
              </h2>
              <p className="text-muted-foreground text-lg md:text-xl leading-relaxed mb-12">
                {t('approach.description')}
              </p>
            </AnimatedSection>
            
            <AnimatedSection className="bg-secondary/30 p-8 md:p-12 rounded-2xl">
              <p className="font-heading text-2xl md:text-3xl text-foreground italic leading-relaxed">
                {t('approach.quote')}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="section-padding bg-background" data-testid="differentiators-section">
        <div className="container-custom">
          <AnimatedSection className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-primary text-sm tracking-[0.2em] uppercase font-medium mb-6">{t('differentiators.label')}</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground">
              {t('differentiators.title')}
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {differentiators.map((item, index) => (
              <AnimatedSection 
                key={item.id}
                className="text-center md:text-left"
                style={{transitionDelay: `${index * 150}ms`}}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-6 mx-auto md:mx-0">
                  <span className="text-primary font-heading font-semibold">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold mb-4 text-foreground">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.description}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section-padding bg-[#0F172A] text-white" data-testid="how-it-works-section">
        <div className="container-custom">
          <AnimatedSection className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-primary text-sm tracking-[0.2em] uppercase font-medium mb-6">{t('howItWorks.label')}</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-white">
              {t('howItWorks.title')}
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <AnimatedSection 
                  key={step.id}
                  className="relative"
                  style={{transitionDelay: `${index * 100}ms`}}
                >
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 h-full hover:bg-white/10 transition-all duration-500">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-primary/80 font-heading text-4xl font-light">{step.number}</span>
                      <Icon size={24} className="text-primary" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-4 text-white">{step.title}</h3>
                    <p className="text-gray-300 leading-relaxed text-sm">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-white/20" />
                  )}
                </AnimatedSection>
              );
            })}
          </div>

          <AnimatedSection className="text-center mt-16">
            <Link to="/start-your-search">
              <Button className="btn-primary btn-ripple h-14 px-10" data-testid="how-it-works-cta">
                {t('howItWorks.cta')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Portugal Section */}
      <section id="why-portugal" className="section-padding bg-white" data-testid="why-portugal-section">
        <div className="container-custom">
          <AnimatedSection className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-primary text-sm tracking-[0.2em] uppercase font-medium mb-6">{t('whyPortugal.label')}</p>
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-foreground mb-8">
              {t('whyPortugal.title')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('whyPortugal.description')}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lifestyles.map((lifestyle, index) => (
              <AnimatedSection 
                key={lifestyle.id}
                animation="reveal-scale"
                style={{transitionDelay: `${index * 100}ms`}}
              >
                <div className="group relative aspect-[3/4] overflow-hidden rounded-2xl">
                  <img 
                    src={lifestyle.image} 
                    alt={lifestyle.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-heading text-xl text-white font-medium">{lifestyle.title}</h3>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Section */}
      <section className="section-padding bg-primary" data-testid="editorial-section">
        <div className="container-custom">
          <AnimatedSection className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-white mb-8 leading-tight">
              {t('editorial.title')}
            </h2>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              {t('editorial.description')}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-background" data-testid="final-cta-section">
        <div className="container-custom">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-foreground mb-6">
              {t('finalCta.title')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-10">
              {t('finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/start-your-search">
                <Button className="btn-primary btn-ripple h-14 px-10" data-testid="final-primary-cta">
                  {t('finalCta.primary')}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="btn-outline h-14 px-10" data-testid="final-secondary-cta">
                  {t('finalCta.secondary')}
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Landing;
